// Mirrors the backend ExternalIdentityDto / UserAuthenticatorsDto (see API.Core.Models.Authentication).

export interface ExternalIdentityInfo {
  id: string;
  provider: string;
  email?: string;
  displayName?: string;
  lastLogin: string;
}

export interface UserAuthenticators {
  userId: string;
  passwordLoginEnabled: boolean;
  authenticators: ExternalIdentityInfo[];
}

/**
 * What kind of thing a {@link BracketedAccount} is. Mirrors `BracketedAccountKind`.
 *
 * **These are NUMBERS on the wire.** The monolith serialises with Newtonsoft (`API/Program.cs` →
 * `AddNewtonsoftJson`) and registers no `StringEnumConverter`, so a C# enum crosses as its ordinal. Comparing
 * `a.kind === 'Employee'` compiles, is always false, and sends every entry down the default branch — which is
 * how every employee row came to be routed at the user-shaped unlink endpoint. Declared in the C# order;
 * append only.
 */
export enum BracketedAccountKind {
  /** The external identity itself, acting as a principal. Filtered out server-side before this list is sent. */
  Identity = 0,
  /** A management `User`. */
  User = 1,
  /** An employee, living in `companyId`'s own database. */
  Employee = 2,
  /**
   * A company this account holds a payroll GRANT for. Not a bracket at all — there is no link row to break, so
   * there is nothing here to unlink; taking that access away is a revoke, and it belongs to the access screen.
   */
  PayrollCompany = 3
}

/** One account (or company) the current external identity brackets. Mirrors `BracketedAccount`. */
export interface BracketedAccount {
  id: string;
  kind: BracketedAccountKind;
  name: string;
  /**
   * The company this entry belongs to. Present for employees, where it is **required** to act on the entry: an
   * employee lives in a per-company database, so {@link id} alone does not identify a row.
   */
  companyId?: string;
  companyName?: string;
  shardName?: string;
  roles: string[];
}
